"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDashboardRemote,
  deleteDashboardRemote,
  fetchDashboards,
  renameDashboardRemote,
  updateDashboardRemote,
} from "@/services/dashboards.service";
import { useCustomerId } from "@/contexts/customer-id-context";
import type { Dashboard } from "@/types/dashboards";

// Customer-scoped so switching customers swaps to that customer's cached list.
export const dashboardsQueryKey = (customerId: string | null) =>
  ["dashboards", customerId] as const;

export function useDashboards() {
  const customerId = useCustomerId();
  return useQuery({
    queryKey: dashboardsQueryKey(customerId),
    queryFn: fetchDashboards,
    enabled: customerId !== null,
  });
}

export function useCreateDashboard() {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      // PostHog-style default: new dashboards are scoped to the last 30 days.
      createDashboardRemote({ ...input, dateRange: { preset: "30d" } }),
    onSuccess: (created) => {
      queryClient.setQueryData<Dashboard[]>(dashboardsQueryKey(customerId), (prev) =>
        prev ? [...prev, created] : [created],
      );
    },
  });
}

export function useRenameDashboard() {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameDashboardRemote(id, name),
    onSuccess: (res, { id, name }) => {
      queryClient.setQueryData<Dashboard[]>(dashboardsQueryKey(customerId), (prev) =>
        prev ? prev.map((d) => (d.id === id ? { ...d, name, updatedAt: res.updatedAt } : d)) : prev,
      );
    },
  });
}

export function useDeleteDashboard() {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();
  return useMutation({
    mutationFn: (id: string) => deleteDashboardRemote(id),
    onSuccess: (_res, id) => {
      // The live subscription also catches the delete echo — filtering an
      // already-removed id is a no-op.
      queryClient.setQueryData<Dashboard[]>(dashboardsQueryKey(customerId), (prev) =>
        prev ? prev.filter((d) => d.id !== id) : prev,
      );
    },
  });
}

export function useSaveDashboard() {
  const queryClient = useQueryClient();
  const customerId = useCustomerId();
  return useMutation({
    mutationFn: (dashboard: Dashboard) => updateDashboardRemote(dashboard),
    onSuccess: (saved, dashboard) => {
      // updateDashboardRemote only returns the new updatedAt; merge it onto the
      // copy we sent and write it back so the view doesn't flash stale data.
      const savedDashboard: Dashboard = { ...dashboard, updatedAt: saved.updatedAt };
      queryClient.setQueryData<Dashboard[]>(dashboardsQueryKey(customerId), (prev) =>
        prev ? prev.map((d) => (d.id === dashboard.id ? savedDashboard : d)) : [savedDashboard],
      );
    },
  });
}
